// Utility for managing try out window settings in local storage

const STORAGE_KEY = "tryout_window_settings";

// Default settings
const DEFAULT_SETTINGS = {
  width: 460,
  height: 500,
  position: null, // Will be set to null for default positioning
  dockDirection: null,
  horLayout: false,
  minified: false,
  resizePosition: 50, // Default 50% split between parameters and body
  labelWidth: 40, // Default 40% width for label column
};

/**
 * Get try out window settings from local storage
 * @returns {Object} The saved settings or default settings
 */
export const getTryoutSettings = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.warn("Failed to load tryout settings from localStorage:", error);
  }
  return DEFAULT_SETTINGS;
};

/**
 * Save try out window settings to local storage
 * @param {Object} settings - The settings to save
 */
export const saveTryoutSettings = (settings) => {
  try {
    const current = getTryoutSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    console.log("[TRYOUT STORAGE] Settings saved:", updated);
  } catch (error) {
    console.warn("Failed to save tryout settings to localStorage:", error);
  }
};

/**
 * Update a specific setting
 * @param {string} key - The setting key
 * @param {any} value - The setting value
 */
export const updateTryoutSetting = (key, value) => {
  saveTryoutSettings({ [key]: value });
};

/**
 * Clear all tryout settings
 */
export const clearTryoutSettings = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear tryout settings from localStorage:", error);
  }
};

/**
 * Save window position
 * @param {number} left - Left position in pixels
 * @param {number} top - Top position in pixels
 */
export const saveTryoutPosition = (left, top) => {
  saveTryoutSettings({
    position: { left, top },
  });
};

/**
 * Save window size
 * @param {number} width - Width in pixels
 * @param {number} height - Height in pixels
 */
export const saveTryoutSize = (width, height) => {
  saveTryoutSettings({ width, height });
};

/**
 * Save dock direction
 * @param {string|null} dockDirection - The dock direction
 */
export const saveTryoutDockDirection = (dockDirection) => {
  saveTryoutSettings({ dockDirection });
};

/**
 * Save layout orientation
 * @param {boolean} horLayout - Whether layout is horizontal
 */
export const saveTryoutOrientation = (horLayout) => {
  saveTryoutSettings({ horLayout });
};

/**
 * Save minified state
 * @param {boolean} minified - Whether window is minified
 */
export const saveTryoutMinified = (minified) => {
  saveTryoutSettings({ minified });
};

/**
 * Save resize handle position
 * @param {number} resizePosition - The percentage width of the parameters section
 */
export const saveTryoutResizePosition = (resizePosition) => {
  saveTryoutSettings({ resizePosition });
};

/**
 * Save label column width
 * @param {number} labelWidth - The percentage width of the label column
 */
export const saveTryoutLabelWidth = (labelWidth) => {
  saveTryoutSettings({ labelWidth });
};
